import { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Toolbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DataGrid, type GridRowModel, type GridRowId } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useApi } from '@/providers/ApiContext';
import { useDocTypes, useDocTypesDispatch } from '@/providers/DocTypesContext';
import { updateDocumentType, deleteDocumentType } from '@/actions/documentType';
import { SET_DOC_TYPES } from '@/providers/actionTypes';
import AdminDocumentTypeForm from './AdminDocumentTypeForm';
import type { DocumentType, EntityId } from '@/types';

function AdminDocumentTypes(): React.JSX.Element {
  const api = useApi();
  const doc_types = useDocTypes();
  const dispatch = useDocTypesDispatch();
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);

  const handleProcessRowUpdate = async (
    newRow: GridRowModel,
    oldRow: GridRowModel,
  ): Promise<GridRowModel> => {
    if (!api) return oldRow;
    try {
      // Update the document type in the API
      await updateDocumentType(api, (newRow as { entity_id?: string })['entity_id'] as EntityId, {
        name: (newRow as { name?: string })['name'] as string,
        code: (newRow as { code?: string })['code'] as string,
        enable_manifests: (newRow as { enable_manifests?: boolean })['enable_manifests'] as boolean,
        enable_charts: (newRow as { enable_charts?: boolean })['enable_charts'] as boolean,
      });

      // Update the local state
      const updatedDocTypes = (doc_types || []).map((docType) =>
        docType.entity_id === (newRow as { entity_id?: string }).entity_id ? (newRow as DocumentType) : docType,
      );
      dispatch({ type: SET_DOC_TYPES, payload: updatedDocTypes });
      return newRow;
    } catch (error) {
      console.error('Error updating document type:', error);
      return oldRow;
    }
  };

  const handleAddClick = (): void => {
    setAddDialogOpen(true);
  };

  const handleAddClose = (): void => {
    setAddDialogOpen(false);
  };

  const handleDeleteClick = (): void => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!api) return;
    try {
      // Delete all selected document types
      await Promise.all(
        selectedRows.map((entity_id) => deleteDocumentType(api, entity_id as EntityId)),
      );

      // Update local state by removing deleted document types
      const updatedDocTypes = (doc_types || []).filter(
        (docType) => !selectedRows.includes(docType.entity_id),
      );
      dispatch({ type: SET_DOC_TYPES, payload: updatedDocTypes });
      setSelectedRows([]);
    } catch (error) {
      console.error('Error deleting document types:', error);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = (): void => {
    setDeleteDialogOpen(false);
  };

  const handleMoveUp = async (): Promise<void> => {
    if (selectedRows.length !== 1 || !doc_types || !api) return;

    const selectedId = selectedRows[0] as string;
    const selectedDoc = doc_types.find((doc) => doc.entity_id === selectedId);
    if (!selectedDoc) return;

    // Find the document type with the next lower level
    const nextDoc = doc_types
      .filter((doc) => doc.level < selectedDoc.level)
      .sort((a, b) => b.level - a.level)[0];

    if (!nextDoc) return;

    try {
      // Swap the levels
      await Promise.all([
        updateDocumentType(api, selectedDoc.entity_id, {
          ...selectedDoc,
          level: nextDoc.level,
        }),
        updateDocumentType(api, nextDoc.entity_id, {
          ...nextDoc,
          level: selectedDoc.level,
        }),
      ]);

      // Update local state
      const updatedDocTypes = doc_types.map((doc) => {
        if (doc.entity_id === selectedDoc.entity_id) {
          return { ...doc, level: nextDoc.level };
        }
        if (doc.entity_id === nextDoc.entity_id) {
          return { ...doc, level: selectedDoc.level };
        }
        return doc;
      });

      dispatch({ type: SET_DOC_TYPES, payload: updatedDocTypes });
    } catch (error) {
      console.error('Error moving document type up:', error);
    }
  };

  const handleMoveDown = async (): Promise<void> => {
    if (selectedRows.length !== 1 || !doc_types || !api) return;

    const selectedId = selectedRows[0] as string;
    const selectedDoc = doc_types.find((doc) => doc.entity_id === selectedId);
    if (!selectedDoc) return;

    // Find the document type with the next higher level
    const nextDoc = doc_types
      .filter((doc) => doc.level > selectedDoc.level)
      .sort((a, b) => a.level - b.level)[0];

    if (!nextDoc) return;

    try {
      // Swap the levels
      await Promise.all([
        updateDocumentType(api, selectedDoc.entity_id, {
          ...selectedDoc,
          level: nextDoc.level,
        }),
        updateDocumentType(api, nextDoc.entity_id, {
          ...nextDoc,
          level: selectedDoc.level,
        }),
      ]);

      // Update local state
      const updatedDocTypes = doc_types.map((doc) => {
        if (doc.entity_id === selectedDoc.entity_id) {
          return { ...doc, level: nextDoc.level };
        }
        if (doc.entity_id === nextDoc.entity_id) {
          return { ...doc, level: selectedDoc.level };
        }
        return doc;
      });

      dispatch({ type: SET_DOC_TYPES, payload: updatedDocTypes });
    } catch (error) {
      console.error('Error moving document type down:', error);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      editable: true,
      sticky: true,
      headerAlign: 'left' as const,
      align: 'left' as const,
    },
    {
      field: 'code',
      headerName: 'Code',
      flex: 1,
      editable: true,
      headerAlign: 'left' as const,
      align: 'left' as const,
    },
    {
      field: 'enable_manifests',
      headerName: 'Enable Manifests',
      type: 'boolean' as const,
      flex: 1,
      editable: true,
      headerAlign: 'left' as const,
      align: 'left' as const,
    },
    {
      field: 'enable_charts',
      headerName: 'Enable Charts',
      type: 'boolean' as const,
      flex: 1,
      editable: true,
      headerAlign: 'left' as const,
      align: 'left' as const,
    },
  ];

  const rows = doc_types || [];

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <Typography variant='h6' sx={{ mr: 4 }}>
          Document Types
        </Typography>
      </Toolbar>
      <Divider />

      <Box sx={{ width: '100%' }} padding='15px'>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            gap: 2,
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Double-click on any cell to edit its value. Changes are saved
            automatically.
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
            }}
          >
            <Toolbar
              variant='dense'
              sx={{
                minHeight: '40px !important',
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderBottom: 'none',
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
              }}
            >
              <Button
                variant='text'
                startIcon={<AddIcon />}
                onClick={handleAddClick}
                sx={{ mr: 1 }}
              >
                Add
              </Button>
              <Button
                variant='text'
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
                disabled={selectedRows.length === 0}
                color='inherit'
                sx={{ mr: 1 }}
              >
                Delete
              </Button>
              <Button
                variant='text'
                startIcon={<ArrowUpwardIcon />}
                onClick={handleMoveUp}
                disabled={selectedRows.length !== 1}
                color='inherit'
                sx={{ mr: 1 }}
              >
                Move Up
              </Button>
              <Button
                variant='text'
                startIcon={<ArrowDownwardIcon />}
                onClick={handleMoveDown}
                disabled={selectedRows.length !== 1}
                color='inherit'
              >
                Move Down
              </Button>
            </Toolbar>
            <DataGrid
              rows={rows}
              columns={columns}
              processRowUpdate={handleProcessRowUpdate}
              disableRowSelectionOnClick
              getRowId={(row) => (row as DocumentType).entity_id}
              sx={(theme) => ({
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.grey[100],
                },
              })}
              onRowSelectionModelChange={(newSelection) => {
                setSelectedRows(Array.from(newSelection.ids));
              }}
              checkboxSelection
              initialState={{
                sorting: {
                  sortModel: [{ field: 'level', sort: 'asc' }],
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby='delete-dialog-title'
        aria-describedby='delete-dialog-description'
      >
        <DialogTitle id='delete-dialog-title'>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id='delete-dialog-description'>
            Are you sure you want to delete the selected document type(s)? This
            action will permanently remove all associated documents and
            manifests, and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            variant='outlined'
            color='inherit'
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant='contained'
            color='primary'
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <AdminDocumentTypeForm
        open={addDialogOpen}
        onClose={handleAddClose}
        doc_types={doc_types || []}
      />
    </>
  );
}

export default AdminDocumentTypes;

